import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import formDataStatic from '@/data/commentsFormData.json';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { FeedbackInput } from '@/components/inputs/Feedback';

import axios from 'axios';
import { DateFormat } from '../common/DateFormat';
import { IComments } from '@/types/types';
import { buildCommentTree } from '@/utils/buildCommentTree';
import { FormModal } from '../common/FormModal';
export interface IFormData {
  comment: string;
  name: string;
  email: string;
}

export const Comments: FC<{
  postId: number;
  commentCount: number | null;
  comments: IComments;
  pt: boolean;
}> = ({ postId, commentCount, comments, pt }): JSX.Element => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isSendErr, setIsSendErr] = useState(false);
  const [language, setLanguage] = useState<'EN' | 'UA'>('UA');
  const { t } = useTranslation('common');

  const formDataValues = formDataStatic[language];

  const {
    register,
    handleSubmit,
    formState: { errors },
    // watch,
    clearErrors,
    reset,

    // formState: { errors, isValid },
    // setValue: setFormValue,
    // trigger,
    // getFieldState,
    // getValues,
    // setError,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<any>({
    criteriaMode: 'all',
    mode: 'onChange',
  });

  const errText = t('formErr');

  const router = useRouter();

  const closeForm = () => {
    setIsOpenModal(false);
  };

  useEffect(() => {
    router.locale == 'en' ? setLanguage('EN') : setLanguage('UA');
  }, [router]);

  const onSubmit: SubmitHandler<IFormData> = async data => {
    try {
      const login = process.env.NEXT_PUBLIC_ADMIN_LOGIN as string;
      const password = process.env.NEXT_PUBLIC_ADMIIN_PASSWORD as string;

      const token = Buffer.from(`${login}:${password}`).toString('base64');

      const authHeader = `Basic ${token}`;

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_URL}/wp-json/wp/v2/comments?post=${postId}&author_name=${data.name}
        &author_email=${data.email}&content=${data.comment}`,
        {},
        {
          headers: {
            Authorization: authHeader,
          },
        },
      );

      if (response.status == 201) {
        setIsSendErr(false);
        setIsOpenModal(true);
        reset();
      } else {
        setIsSendErr(true);
      }
    } catch (error) {
      setIsSendErr(true);
      console.log(error, 'err');
    }
  };

  const commentTree =
    comments.nodes.length > 0 ? buildCommentTree(comments) : [];

  return (
    <AnimatedCard>
      <div
        className={`bg-white dark:bg-black ${
          pt ? 'pt-8 md:pt-9 lg:pt-16' : ''
        } `}
      >
        <div className="mx-auto px-4">
          <div className="mb-6 flex items-center justify-between">
            <p className="lg:text-2xl text-lg font-bold text-primaryDark dark:text-darkFullLight">
              {formDataValues.title} ({commentCount ? commentCount : 0})
            </p>
          </div>
          <form className="mb-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <FeedbackInput
                required={true}
                type={'textarea'}
                name={'comment'}
                register={register}
                placeholder={formDataValues.textarea}
                onChange={() => clearErrors('comment')}
                locale={language}
              />{' '}
              {errors?.comment?.message && (
                <p className="pt-0.5 text-error">
                  {errors.comment.message.toString()}
                </p>
              )}
            </div>
            <div className="mb-4 flex w-full">
              {' '}
              <div className="mr-4 w-full">
                <FeedbackInput
                  required={true}
                  type={'text'}
                  name={'name'}
                  register={register}
                  placeholder={formDataValues.name}
                  onChange={() => clearErrors('name')}
                  locale={language}
                />{' '}
                {errors?.name?.message && (
                  <p className="pt-0.5 text-error">
                    {errors.name.message.toString()}
                  </p>
                )}
              </div>{' '}
              <div className=" w-full">
                <FeedbackInput
                  required={true}
                  type={'email'}
                  name={'email'}
                  register={register}
                  placeholder={formDataValues.email}
                  onChange={() => clearErrors('email')}
                  locale={language}
                />{' '}
                {errors?.email?.message && (
                  <p className="pt-0.5 text-error">
                    {errors.email.message.toString()}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="focus:ring-accentAdditional-300 dark:bg-accentAdditional-600 dark:focus:ring-accentAdditional-800 dark:hover:bg-accentAdditional-700 w-full rounded-lg bg-accentMain px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-accentAdditional focus:outline-none  sm:w-auto"
            >
              {formDataValues.submit.label}
            </button>
            {isSendErr ? <p className="pt-0.5 text-error">{errText}</p> : null}
          </form>
          {commentTree.length > 0 ? (
            commentTree.map(item => (
              <div
                key={item.id}
                className="md:p6 rounded-lg bg-white p-3 text-base dark:bg-black"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center">
                    <p className="!mb-0 mr-3 inline-flex items-center text-sm font-semibold text-primaryDark dark:text-darkFullLight">
                      <Image
                        src={
                          item.author.node.avatar.url ?? '/favicon-32x32.png'
                        }
                        className="mr-2 h-6 w-6 rounded-full"
                        alt={'Guest avatar'}
                        width={item.author.node.avatar.width}
                        height={item.author.node.avatar.height}
                      />
                      {item.author.node.name}
                    </p>
                    <p className="text-sm text-primaryMiddle dark:text-darkFullLight">
                      <DateFormat date={item.date} />
                    </p>
                  </div>
                </div>
                <p
                  className="text-primaryMiddle dark:text-darkFullLight"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />

                {item.replies && item.replies.length > 0
                  ? item.replies.map(childItem => (
                      <div
                        key={childItem.id}
                        className="md:p6 mb-3 ml-3 rounded-lg bg-white p-3 text-base dark:bg-black md:ml-6 lg:ml-12"
                      >
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center">
                            <p className="!mb-0 mr-3 inline-flex items-center text-sm font-semibold text-primaryDark dark:text-darkFullLight">
                              <Image
                                className="mr-2 h-6 w-6 rounded-full"
                                src={'/favicon-32x32.png'}
                                alt={'Admin avatar'}
                                width={item.author.node.avatar.width}
                                height={item.author.node.avatar.height}
                              />
                              {childItem.author.node.name}
                            </p>
                            <p className="text-sm text-primaryMiddle dark:text-darkFullLight">
                              <time title="February 12th, 2022">
                                <DateFormat date={childItem.date} />
                              </time>
                            </p>
                          </div>{' '}
                        </div>

                        <p
                          className="text-primaryMiddle dark:text-darkFullLight"
                          dangerouslySetInnerHTML={{
                            __html: childItem.content,
                          }}
                        />
                      </div>
                    ))
                  : null}
              </div>
            ))
          ) : (
            <div className="lg:text-2xl text-center text-lg font-bold text-primaryDark dark:text-darkFullLight">
              {t('noComments')}
            </div>
          )}
        </div>
      </div>{' '}
      <FormModal
        closeModal={closeForm}
        isOpen={isOpenModal}
        text={formDataValues.thankText}
      />
    </AnimatedCard>
  );
};
